import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import classNames from 'classnames';

import { ModalHOC } from 'common/HOCs/ModalHOC';
import { DefaultButton, NoItemsPanel } from 'common/components';
import { taggedStylesToString, withTheme } from 'common/styling/theme';

// import { BasketIcon } from 'common/icons';
import FormFieldsComposer from '../FormFieldsComposer';

import { getDynamicStyles, staticStyles } from './style';
import AccountGroupArrayField from './index';

class GroupArrayField extends Component {
  // 深拷贝函数，不会序列化函数
  static deepClone(obj) {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }
    if (obj instanceof Date) {
      return new Date(obj.getTime());
    }
    if (obj instanceof Array) {
      return obj.map(item => AccountGroupArrayField.deepClone(item));
    }
    if (typeof obj === 'object') {
      const clonedObj = {};
      Object.keys(obj).forEach(key => {
        clonedObj[key] = AccountGroupArrayField.deepClone(obj[key]);
      });
      return clonedObj;
    }
  }

  // 动态创建包含condition字段的schema
  static createDynamicSchema(originalSchema, conditions) {
    if (!originalSchema || !originalSchema.items || !conditions || conditions.length === 0) {
      return originalSchema;
    }
    // 创建schema的深拷贝
    const dynamicSchema = AccountGroupArrayField.deepClone(originalSchema);
    // 确保fields对象存在
    if (!dynamicSchema.items.properties) {
      dynamicSchema.items.properties = {};
    }

    // 为每个condition添加字段定义
    conditions.forEach((condition, index) => {
      const fieldName = `condition${index + 1}`;
      const conditionLabel = typeof condition === 'string' ? condition : `Condition ${index + 1}`;

      dynamicSchema.items.properties[fieldName] = {
        widget: 'number',
        title: conditionLabel,
        default: 0,
        minimum: 0,
        required: true,
        disabled: false,
      };
    });

    // 设置name字段为只读
    if (dynamicSchema.items.properties.name) {
      dynamicSchema.items.properties.name.disabled = true;
    }
    return dynamicSchema;
  }

  // 保留所有字段，包括name字段（已设为只读）
  static filterSchemaFields(schema) {
    if (!schema || !schema.items || !schema.items.fields) return schema;
    // 创建schema的深拷贝
    const filteredSchema = AccountGroupArrayField.deepClone(schema);
    // 保留所有字段，不再过滤name字段
    return filteredSchema;
  }

  constructor(props) {
    super(props);
    // 在组件初始化时调用初始化方法
    this.state = {
      isInitialized: false,
    };
  }

  componentDidMount() {
    // 页面初始化时获取所有值
    this.initializePageData();
  }

  componentDidUpdate(prevProps) {
    // 当formData发生变化时，重新初始化数据
    if (prevProps.formData !== this.props.formData && !this.state.isInitialized) {
      this.initializePageData();
    }
  }

  // 页面初始化方法，获取页面所有值
  initializePageData = () => {
    const { fields, formData } = this.props;
    // 尝试获取选中的group items和conditions
    let selectedGroups = [];

    // 主路径：从special_account.group_exceptions获取
    if (formData?.special_account?.group_exceptions && formData.special_account.group_exceptions.length > 0) {
      const firstException = formData.special_account.group_exceptions[0];
      selectedGroups = firstException?.value?.item || [];
    }

    // 如果已有数据且fields为空，则自动填充
    if (selectedGroups.length > 0 && fields.length === 0) {
      this.handleNextButtonClick();
    }
    // 标记为已初始化
    this.setState({ isInitialized: true });
  };

  onDragEnd = result => {
    const { fields } = this.props;

    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const startIndex = result.source.index;
    const endIndex = result.destination.index;
    const [removed] = fields.getAll().splice(startIndex, 1);

    fields.splice(endIndex, 0, removed);
  };

  // Next按钮
  handleNextButtonClick = () => {
    const { fields, formData } = this.props;

    // 尝试多种可能的路径获取选中的group items
    let selectedGroups = [];
    let conditions = [];

    // 主路径：从special_account.group_exceptions获取
    if (formData?.special_account?.group_exceptions && formData.special_account.group_exceptions.length > 0) {
      const firstException = formData.special_account.group_exceptions[0];
      selectedGroups = firstException?.value?.item || [];
      conditions = firstException?.value?.value?.condition || [];
    }
    const rewardAmount = formData?.special_account?.group_exceptions?.[0]?.value?.value?.reward_amount;
    console.log('rewardAmount', rewardAmount);
    // 定义rewardAmountObj，由rewardAmount遍历而来，key为rewardAmount的name字段
    const rewardAmountObj = {};
    if (Array.isArray(rewardAmount)) {
      rewardAmount.forEach(item => {
        if (item && item.name) {
          rewardAmountObj[item.name] = item;
        }
      });
    }
    console.log('rewardAmountObj', rewardAmountObj);
    // 清空现有的所有字段，重新生成数据
    const currentFields = fields.getAll() || [];
    // 从后往前删除，避免索引变化问题
    for (let i = currentFields.length - 1; i >= 0; i -= 1) {
      fields.remove(i);
    }
    // 为每个选中的group重新创建奖励条目
    selectedGroups.forEach(group => {
      // 确保group是有效的字符串
      if (typeof group === 'string' && group.trim()) {
        const newItem = {
          name: group, // 设置name为选中的group名称
        };

        // 为每个condition添加默认值
        conditions.forEach((_, index) => {
          // 如果表单里有数据，则这里直接赋值表单的数据，否则为0
          const conditionKey = `condition${index + 1}`;
          const existingValue = rewardAmountObj?.[group]?.[conditionKey];
          console.log('existingValue', existingValue);
          newItem[conditionKey] = existingValue !== undefined ? existingValue : 0;
        });
        fields.push(newItem);
      } else {
        console.log(`Skipping invalid group: ${group}`);
      }
    });
  };

  render() {
    const {
      schema,
      fields,
      meta: { error, pristine },
      formName,
      arraysName,
      submitting,
      theme,
      formData,
    } = this.props;

    const style = taggedStylesToString(staticStyles);
    const dynamicStyles = Object.keys(theme).length ? taggedStylesToString(getDynamicStyles(theme)) : ` `;

    const formError = !pristine && error;
    const isDragDisabled = !schema.ordering || fields.length === 1;
    const isFormError = typeof error === 'object';

    // 获取conditions数据
    let conditions = [];
    if (formData?.special_account?.group_exceptions && formData.special_account.group_exceptions.length > 0) {
      const firstException = formData.special_account.group_exceptions[0]?.value;
      conditions = firstException?.value?.condition || [];
    }
    // 创建动态schema，包含condition字段
    const dynamicSchema = GroupArrayField.createDynamicSchema(schema, conditions);

    // 过滤schema，移除name字段
    const filteredSchema = GroupArrayField.filterSchemaFields(dynamicSchema);
    return (
      <div className="FieldArray" id={arraysName}>
        <div className="FieldArray__top-line">
          <div className="FieldArray__title">
            <FormattedMessage id={schema.title}>{txt => <h2>{txt}</h2>}</FormattedMessage>
          </div>
          <DefaultButton textId="justNext" type="button" onClick={this.handleNextButtonClick} />
        </div>

        {fields.length ? (
          <DragDropContext onDragEnd={this.onDragEnd}>
            <Droppable droppableId="droppable" isDropDisabled={isDragDisabled}>
              {(provided, snapshot) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className={classNames('FieldArray__isDragging-inner', { isDraggingOver: snapshot.isDraggingOver })}>
                  {fields.map((item, index) => (
                    <Draggable
                      key={`${index}_${fields.length}`}
                      draggableId={item}
                      index={index}
                      isDragDisabled={isDragDisabled}>
                      {(provided, snapshot) => (
                        <div
                          className={classNames('FieldArray__item-inner', {
                            FieldArray__notDraggable: !schema.ordering,
                            FieldArray__isDraggable: snapshot.isDragging,
                            FieldArray__item_error: isFormError && error[index],
                          })}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={provided.draggableProps.style}>
                          <div className="FieldArray__item">
                            <p className="FieldArray__item-number">{index + 1}</p>
                            <FormFieldsComposer
                              name={`${item}`}
                              schema={filteredSchema.items}
                              formName={formName}
                              submitting={submitting}
                            />
                            {isFormError && error[index] && (
                              <FormattedMessage id={error[index]}>
                                {txt => <span className="ArrayField__item-error">{txt}</span>}
                              </FormattedMessage>
                            )}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          <NoItemsPanel
            // captionSlug={schema.title}
            // descriptionSlug={schema.description}
            className="FieldArray__no-items"
            isError={!!formError}
          />
        )}

        <style>{style}</style>
        <style>{dynamicStyles}</style>
      </div>
    );
  }
}

GroupArrayField.propTypes = {
  schema: PropTypes.object.isRequired,
  formName: PropTypes.string.isRequired,
  showModal: PropTypes.func.isRequired,
  meta: PropTypes.object.isRequired,
  fields: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  arraysName: PropTypes.string.isRequired,
  submitting: PropTypes.bool,
  theme: PropTypes.object,
  formData: PropTypes.object,
};

GroupArrayField.defaultProps = {
  fields: {},
  submitting: false,
  theme: {},
  formData: {},
};

// 使用connect从Redux store获取表单数据
const mapStateToProps = (state, ownProps) => {
  const formData = state.form && state.form[ownProps.formName] ? state.form[ownProps.formName].values || {} : {};
  return {
    formData,
  };
};

const ThemedArrayField = compose(connect(mapStateToProps), withTheme(), ModalHOC)(GroupArrayField);

export default ThemedArrayField;
export { GroupArrayField };
